import type { KeyboardEvent } from "react";
import { useState } from "react";

import { Check, ChevronDown } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { ClassifiedDomain, CustomIndustryAgentState } from "../../CustomHooks/customIndustryAgent.types";
import { AGENT_STEPS } from "../../CustomHooks/customIndustryAgent.types";
import { useCustomIndustryAgent } from "../../CustomHooks/useCustomIndustryAgent";
import { getStepLabel } from "./stepLabels";
import { currentStepIndex, isStepSkipped } from "./stepPlan";
import {
    AI_GRADIENT_CHECK_TEAL,
    ChevronRotator,
    GradientCheckIcon,
    GradientDefsSvg,
    ShimmerLabel,
    StepDetailCaption,
    StepDetailList,
    StepIconSlot,
    StepLabel,
    StepLoaderRing,
    StepRowHeader,
} from "./styledComponents";
import type { StepRowStatus } from "./styledComponents";
import { translateWithFallback as t } from "./translateWithFallback";

const { blue, carbon } = colorsPalettes;

const GRADIENT_CHECK_ID = "market-builder-v2-ai-gradient-check";

type AgentStep = (typeof AGENT_STEPS)[number];

// `@similarweb/icons` has no gradient-fill icon variant, so the checkmark's fill is repainted
// via this shared SVG gradient def (see `GradientCheckIcon` in styledComponents) — rendered
// once, off-screen, and referenced by every "done" row through `fill: url(#...)`.
const GradientDefs = () => (
    <GradientDefsSvg
        width="0"
        height="0"
        aria-hidden="true"
    >
        <defs>
            <linearGradient
                id={GRADIENT_CHECK_ID}
                x1="0"
                y1="0"
                x2="1"
                y2="0"
            >
                <stop
                    offset="0%"
                    stopColor={blue[400]}
                />
                <stop
                    offset="100%"
                    stopColor={AI_GRADIENT_CHECK_TEAL}
                />
            </linearGradient>
        </defs>
    </GradientDefsSvg>
);

const getRowStatus = (
    index: number,
    completedSteps: number,
    state: CustomIndustryAgentState,
    maxSegments: number,
): StepRowStatus => {
    if (isStepSkipped(AGENT_STEPS[index], maxSegments)) {
        return "skipped";
    }
    if (index < completedSteps) {
        return "done";
    }
    if (index === currentStepIndex(completedSteps, maxSegments) && state === "generating") {
        return "active";
    }
    return "pending";
};

// Figma node 50:2759's 40px icon slot now holds three states: a gradient checkmark once done,
// a spinning ring while active, and nothing for upcoming or skipped rows.
const StatusIcon = ({ status }: { status: StepRowStatus }) => (
    <StepIconSlot>
        {status === "done" && (
            <GradientCheckIcon>
                <Check
                    size="sm"
                    color={blue[400]}
                />
            </GradientCheckIcon>
        )}
        {status === "active" && <StepLoaderRing />}
    </StepIconSlot>
);

type StepDetailContext = {
    discoveredDomains: ClassifiedDomain[] | null;
    topicSummary: string | null;
    trafficFailures: string[] | null;
    createdSegments: ClassifiedDomain[] | null;
    rulesGenerated: number;
};

type StepDetail = { kind: "caption"; text: string } | { kind: "list"; items: string[]; testId: string };

// Nothing upstream dedupes an update, so a node that re-reports a domain would otherwise
// hand the same React key to two rows.
const uniqueItems = (items: string[]): string[] => Array.from(new Set(items));

// Every row surfaces a clean, human-readable summary of its own backend node update as soon
// as one arrives — this is what makes "what's happening under the hood" visible for the whole
// run, not just a single hardcoded step.
const getStepDetail = (step: AgentStep, context: StepDetailContext): StepDetail | null => {
    if (step === "normalize_topic") {
        return context.topicSummary ? { kind: "caption", text: context.topicSummary } : null;
    }

    if (step === "verify_traffic") {
        return context.trafficFailures?.length
            ? {
                  kind: "list",
                  items: uniqueItems(context.trafficFailures),
                  testId: "market-builder-v2-traffic-failures",
              }
            : null;
    }

    if (step === "classify_domains") {
        return context.discoveredDomains?.length
            ? {
                  kind: "list",
                  items: uniqueItems(context.discoveredDomains.map((domain) => domain.domain)),
                  testId: "market-builder-v2-discovered-domains",
              }
            : null;
    }

    if (step === "create_segments") {
        if (context.createdSegments?.length) {
            return {
                kind: "list",
                items: uniqueItems(context.createdSegments.map((segment) => segment.domain)),
                testId: "market-builder-v2-created-segments",
            };
        }

        if (context.rulesGenerated > 0) {
            const total = context.discoveredDomains?.length ?? 0;
            const text = total
                ? t("market.builder.v2.progress.rules.ofTotal", "Building segment rules — %done% of %total%", {
                      done: context.rulesGenerated,
                      total,
                  })
                : t("market.builder.v2.progress.rules.done", "Building segment rules — %done% done", {
                      done: context.rulesGenerated,
                  });
            return { kind: "caption", text };
        }

        return null;
    }

    return null;
};

export const StepChecklist = () => {
    const {
        state,
        completedSteps,
        maxSegments,
        discoveredDomains,
        topicSummary,
        trafficFailures,
        createdSegments,
        rulesGenerated,
    } = useCustomIndustryAgent();

    // Figma node 141:6100 — a completed step's detail (the domains it found, the topic it
    // read) collapses behind its chevron once the run has moved on; only the step actively
    // being worked on keeps its detail open by default.
    const [expandedSteps, setExpandedSteps] = useState<Set<AgentStep>>(new Set());

    const toggleStep = (step: AgentStep) =>
        setExpandedSteps((previous) => {
            const next = new Set(previous);
            if (next.has(step)) {
                next.delete(step);
            } else {
                next.add(step);
            }
            return next;
        });

    const handleToggleKeyDown = (step: AgentStep) => (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleStep(step);
        }
    };

    const rows = AGENT_STEPS.map((step, index) => ({
        step,
        label: getStepLabel(step),
        status: getRowStatus(index, completedSteps, state, maxSegments),
        detail: getStepDetail(step, {
            topicSummary,
            trafficFailures,
            discoveredDomains,
            createdSegments,
            rulesGenerated,
        }),
    }));

    return (
        <Flex
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
            data-automation="market-builder-v2-step-checklist"
        >
            <GradientDefs />
            {rows.map((row) => {
                const isToggleable = row.status === "done" && row.detail !== null;
                // Only a completed row's detail hides by default — the active step (nothing to
                // collapse back to yet) and a pending step's rare early detail both stay open,
                // matching the checklist's behavior before collapsing existed.
                const isExpanded = row.status !== "skipped" && (row.status !== "done" || expandedSteps.has(row.step));

                return (
                    <Flex
                        key={row.step}
                        flexDirection="column"
                        alignItems="flex-start"
                        width="100%"
                    >
                        <StepRowHeader
                            $clickable={isToggleable}
                            role={isToggleable ? "button" : undefined}
                            tabIndex={isToggleable ? 0 : undefined}
                            aria-expanded={isToggleable ? expandedSteps.has(row.step) : undefined}
                            onClick={isToggleable ? () => toggleStep(row.step) : undefined}
                            onKeyDown={isToggleable ? handleToggleKeyDown(row.step) : undefined}
                            data-automation={isToggleable ? `market-builder-v2-step-toggle-${row.step}` : undefined}
                        >
                            <StatusIcon status={row.status} />
                            {row.status === "active" ? (
                                <ShimmerLabel data-automation="market-builder-v2-step-active">{row.label}</ShimmerLabel>
                            ) : (
                                <StepLabel $status={row.status}>{row.label}</StepLabel>
                            )}
                            {row.status === "skipped" && (
                                <Tooltip
                                    content={t(
                                        "market.builder.v2.progress.skipped.tooltip",
                                        "Segments are not in your plan. Contact sales for more.",
                                    )}
                                    placement="top"
                                    data-automation="market-builder-v2-step-skipped"
                                >
                                    <Badge
                                        type="neutral"
                                        shouldTransformTextToUpperCase
                                    >
                                        {t("market.builder.v2.progress.skipped.badge", "Skipped")}
                                    </Badge>
                                </Tooltip>
                            )}
                            {isToggleable && (
                                <ChevronRotator $expanded={expandedSteps.has(row.step)}>
                                    <ChevronDown
                                        size="sm"
                                        color={carbon[300]}
                                    />
                                </ChevronRotator>
                            )}
                        </StepRowHeader>
                        {isExpanded && row.detail?.kind === "caption" && (
                            <StepDetailCaption>{row.detail.text}</StepDetailCaption>
                        )}
                        {isExpanded && row.detail?.kind === "list" && (
                            <StepDetailList data-automation={row.detail.testId}>
                                {row.detail.items.map((item) => (
                                    <Text
                                        key={item}
                                        fontVariant="body2"
                                        color={blue[400]}
                                    >
                                        {item}
                                    </Text>
                                ))}
                            </StepDetailList>
                        )}
                    </Flex>
                );
            })}
        </Flex>
    );
};
